import { createImageUrlBuilder } from '@sanity/image-url'
import type { SanityImageSource } from '@sanity/image-url'
import { dataset, projectId } from '@/sanity/env'

/**
 * Sanity Image URL Builder
 *
 * Replaces src/lib/cloudinary.ts with Sanity CDN equivalents.
 * Uses @sanity/image-url to generate optimized image URLs with
 * automatic format negotiation, resizing, and hotspot-aware cropping.
 */

const builder = createImageUrlBuilder({ projectId, dataset })

/**
 * Create a chainable Sanity image URL builder instance.
 * Use for custom transforms: sanityImageUrl(source).width(800).height(600).url()
 */
export function sanityImageUrl(source: SanityImageSource) {
  return builder.image(source)
}

export interface SanityImageUrlOptions {
  width?: number
  height?: number
  quality?: number
  blur?: number
  format?: 'jpg' | 'pjpg' | 'png' | 'webp' | 'avif' | 'auto'
  fit?: 'clip' | 'crop' | 'fill' | 'fillmax' | 'max' | 'scale' | 'min'
}

/**
 * Build a Sanity CDN image URL with transformations.
 * Replaces buildCloudinaryUrl.
 */
export function buildSanityImageUrl(
  source: SanityImageSource,
  options: SanityImageUrlOptions = {}
): string {
  const { width, height, quality = 80, blur, fit = 'max', format = 'auto' } = options

  let img = builder.image(source)

  if (width) img = img.width(width)
  if (height) img = img.height(height)
  if (fit) img = img.fit(fit)
  if (quality) img = img.quality(quality)
  if (blur) img = img.blur(blur)
  // @sanity/image-url@2.1.1 typedefs omit avif; cast to satisfy the builder while
  // still emitting fm=avif in the URL (the CDN fully supports AVIF at runtime).
  if (format && format !== 'auto') img = img.format(format as 'jpg' | 'pjpg' | 'png' | 'webp')
  // Note: auto format is handled by Sanity CDN via ?auto=format in the loader

  return img.auto('format').url()
}

/**
 * Build a Sanity CDN URL for OG/Twitter meta images (fill crop, 1200x630).
 * Replaces buildCloudinaryOgUrl.
 */
export function buildSanityOgUrl(
  source: SanityImageSource,
  dimensions = { width: 1200, height: 630 }
): string {
  return builder
    .image(source)
    .width(dimensions.width)
    .height(dimensions.height)
    .fit('crop')
    .auto('format')
    .url()
}

/**
 * Build a raster PNG Sanity CDN URL for Satori/next/og, which cannot consume
 * SVG or always-negotiate webp. Forces png + a fixed width.
 */
export function buildSanityImagePng(source: SanityImageSource, width = 600): string {
  return builder.image(source).width(width).format('png').url()
}

/**
 * Build a raw Sanity CDN URL with no transforms (for JSON-LD images).
 * Includes `?auto=format` so crawlers advertising image/webp receive WebP/AVIF.
 * Replaces buildCloudinaryRawUrl.
 */
export function buildSanityRawUrl(source: SanityImageSource): string {
  return builder.image(source).auto('format').url()
}
